import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ProfileService, type Profile } from '../entities/Profile';
import i18n from '../i18n';
import supabase from '../utils/supabase';

export interface ProfileSettingsState {
  // Profile data
  profile: Profile | null;
  profileLoading: boolean;
  profileError: string | null;

  // Profile statistics
  totalCalls: number;
  lastActivity: string | null;
  termsAcceptedAt: string | null;

  // VoiceCenter credentials
  voicenterLogin: string;
  voicecenterPassword: string;

  // Device ID configuration
  defaultDeviceIdSalesman: string;
  defaultDeviceIdClient: string;

  // Transcription visibility
  transcriptionVisible: boolean;

  // Microphone gate threshold (dBFS, range -80..-20). User-tuned via the mic
  // level meter. Persisted on Profile and sent to Go on call start.
  micMinDbFS: number;

  // Call Provider settings
  callProvider: 'voicenter' | 'tochat' | 'squaretalk';


  workingPhone: string;
  squaretalkClientDomain: string;
  squaretalkExtension: string;
  language: string;

  // Agent settings
  isResponseNeededEnabled: boolean;
  agentSalesStageEnabled: boolean;
  agentSpeechToneEnabled: boolean;
  agentSummarizerEnabled: boolean;

  // UI state
  savingCredentials: boolean;
  credentialsError: string | null;
  credentialsSuccess: string | null;
}

export interface ProfileSettingsActions {
  // Profile management
  fetchProfile: (userId: string) => Promise<void>;
  updateProfile: (userId: string, role: string) => Promise<void>;

  // VoiceCenter credentials
  setVoicenterLogin: (login: string) => void;
  setVoicecenterPassword: (password: string) => void;

  // Device configuration
  setDefaultDeviceIdSalesman: (deviceId: string) => void;
  setDefaultDeviceIdClient: (deviceId: string) => void;

  // Transcription settings
  setTranscriptionVisible: (visible: boolean) => void;

  // Mic gate threshold — set immediately for UI responsiveness; commitMicMinDbFS persists to DB.
  setMicMinDbFS: (value: number) => void;
  commitMicMinDbFS: (userId: string, value: number) => Promise<void>;

  // Call Provider settings
  setCallProvider: (provider: 'voicenter' | 'tochat' | 'squaretalk') => void;


  setWorkingPhone: (phone: string) => void;
  setSquaretalkClientDomain: (domain: string) => void;
  setSquaretalkExtension: (extension: string) => void;
  setLanguage: (lang: string) => void;

  // Agent settings
  setIsResponseNeededEnabled: (enabled: boolean) => void;
  setAgentSalesStageEnabled: (enabled: boolean) => void;
  setAgentSpeechToneEnabled: (enabled: boolean) => void;
  setAgentSummarizerEnabled: (enabled: boolean) => void;

  // UI state management
  setSavingCredentials: (saving: boolean) => void;
  setCredentialsError: (error: string | null) => void;
  setCredentialsSuccess: (success: string | null) => void;

  // Reset functions
  resetCredentialsState: () => void;
  resetProfile: () => void;
  acceptTerms: () => Promise<void>;
}

export type ProfileSettingsStore = ProfileSettingsState & ProfileSettingsActions;

export const useProfileSettingsStore = create<ProfileSettingsStore>()(persist<ProfileSettingsStore>((set, get) => ({
  // Initial state
  profile: null,
  profileLoading: false,
  profileError: null,
  totalCalls: 0,
  lastActivity: null,
  termsAcceptedAt: null,
  voicenterLogin: '',
  voicecenterPassword: '',
  defaultDeviceIdSalesman: '',
  defaultDeviceIdClient: '',
  transcriptionVisible: false,
  micMinDbFS: -48,
  callProvider: 'voicenter',


  workingPhone: '',
  squaretalkClientDomain: '',
  squaretalkExtension: '',
  language: 'he',
  isResponseNeededEnabled: false,
  agentSalesStageEnabled: false,
  agentSpeechToneEnabled: false,
  agentSummarizerEnabled: false,
  savingCredentials: false,
  credentialsError: null,
  credentialsSuccess: null,

  // Profile management actions
  fetchProfile: async (userId: string) => {
    set({ profileLoading: true, profileError: null });

    try {
      const profileService = new ProfileService();
      const profileData = await profileService.getByUserId(userId);

      if (profileData) {
        set({
          profile: profileData,
          voicenterLogin: profileData.voicenter_login || '',
          voicecenterPassword: profileData.voicecenter_password || '',
          defaultDeviceIdSalesman: profileData.default_device_id_salesman || '',
          defaultDeviceIdClient: profileData.default_device_id_client || '',
          transcriptionVisible: profileData.transcription_visible ?? false,
          micMinDbFS: typeof profileData.mic_min_db_fs === 'number' ? profileData.mic_min_db_fs : -48,
          callProvider: profileData.call_provider || 'voicenter',


          workingPhone: profileData.workingPhone || '',
          squaretalkClientDomain: profileData.Center?.squaretalk_domain || profileData.squaretalkClientDomain || '',
          squaretalkExtension: profileData.squaretalkExtension || '',
          language: profileData.language || i18n.language || 'he',
          isResponseNeededEnabled: profileData.is_response_needed_enabled ?? false,
          agentSalesStageEnabled: profileData.agent_sales_stage_enabled ?? false,
          agentSpeechToneEnabled: profileData.agent_speech_tone_enabled ?? false,
          agentSummarizerEnabled: profileData.agent_summarizer_enabled ?? false,
          termsAcceptedAt: profileData.terms_accepted_at || null,
          profileLoading: false,
          profileError: null,
        });

        // Sync i18n only when DB has an explicitly saved language. If the DB column
        // is null/empty, the user hasn't persisted a choice yet — keep whatever i18n
        // already has (loaded from localStorage at app boot) instead of overwriting it.
        if (profileData.language && i18n.language !== profileData.language) {
          i18n.changeLanguage(profileData.language);
          localStorage.setItem('language', profileData.language);
        }
      } else {
        set({
          profile: null,
          profileLoading: false,
          profileError: null,
        });
      }
    } catch (error) {
      set({
        profileLoading: false,
        profileError: error instanceof Error ? error.message : 'Failed to fetch profile',
      });
    }
  },

  updateProfile: async (userId: string, role: string) => {
    const state = get();
    set({ savingCredentials: true, credentialsError: null, credentialsSuccess: null });

    try {
      const profileService = new ProfileService();
      const updatedProfile = await profileService.upsertForUser(
        userId,
        role,
        state.voicenterLogin.trim() === '' ? null : state.voicenterLogin.trim(),
        state.voicecenterPassword.trim() === '' ? null : state.voicecenterPassword.trim(),
        state.defaultDeviceIdSalesman.trim() === '' ? null : state.defaultDeviceIdSalesman.trim(),
        state.defaultDeviceIdClient.trim() === '' ? null : state.defaultDeviceIdClient.trim(),
        state.transcriptionVisible,
        state.isResponseNeededEnabled,
        state.agentSalesStageEnabled,
        state.agentSpeechToneEnabled,
        state.agentSummarizerEnabled,
        state.callProvider,
        state.workingPhone.trim() === '' ? null : state.workingPhone.trim(),
        state.squaretalkClientDomain.trim() === '' ? null : state.squaretalkClientDomain.trim(),
        state.squaretalkExtension.trim() === '' ? null : state.squaretalkExtension.trim(),

      );

      if (updatedProfile) {
        // Check if Squaretalk is selected and trigger webhook
        if (updatedProfile.call_provider === 'squaretalk' && updatedProfile.squaretalkClientDomain) {
          try {
            await fetch('https://hook.eu2.make.com/qf79h2ehd1d3z7pxn6u3c1ci9b2jd26b', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                clientDomain: updatedProfile.squaretalkClientDomain,
              }),
            });
            console.log('Squaretalk configuration webhook triggered successfully');
          } catch (webhookError) {
            console.error('Failed to trigger Squaretalk webhook:', webhookError);
            // We don't fail the overall save if webhook fails, but maybe log it
          }
        }

        set({
          profile: updatedProfile,
          savingCredentials: false,
          credentialsSuccess: 'Profile updated successfully',
        });
      } else {
        set({
          savingCredentials: false,
          credentialsError: 'Failed to update profile - no response from service',
        });
      }
    } catch (error) {
      set({
        savingCredentials: false,
        credentialsError: error instanceof Error ? error.message : 'Failed to update profile',
      });
    }
  },

  // VoiceCenter credentials actions
  setVoicenterLogin: (login: string) => set({ voicenterLogin: login }),
  setVoicecenterPassword: (password: string) => set({ voicecenterPassword: password }),

  // Device configuration actions
  setDefaultDeviceIdSalesman: (deviceId: string) => set({ defaultDeviceIdSalesman: deviceId }),
  setDefaultDeviceIdClient: (deviceId: string) => set({ defaultDeviceIdClient: deviceId }),

  // Transcription settings actions
  setTranscriptionVisible: (visible: boolean) => set({ transcriptionVisible: visible }),

  // Mic gate threshold actions: setMicMinDbFS updates state immediately for
  // smooth slider interaction; commitMicMinDbFS persists to the DB on release.
  setMicMinDbFS: (value: number) => {
    const clamped = Math.max(-80, Math.min(-20, value));
    set({ micMinDbFS: clamped });
  },
  commitMicMinDbFS: async (userId: string, value: number) => {
    const clamped = Math.max(-80, Math.min(-20, value));
    set({ micMinDbFS: clamped });
    try {
      const profileService = new ProfileService();
      const updated = await profileService.updateMicMinDbFs(userId, clamped);
      if (updated) set({ profile: updated });
    } catch (err) {
      console.error('[ProfileSettings] Failed to persist micMinDbFS:', err);
    }
  },

  // Call Provider settings actions
  setCallProvider: (provider: 'voicenter' | 'tochat' | 'squaretalk') => {
    set({ callProvider: provider });
  },

  setWorkingPhone: (phone: string) => set({ workingPhone: phone }),
  setSquaretalkClientDomain: (domain: string) => set({ squaretalkClientDomain: domain }),
  setSquaretalkExtension: (extension: string) => set({ squaretalkExtension: extension }),
  setLanguage: (lang: string) => set({ language: lang }),

  // Agent settings actions
  setIsResponseNeededEnabled: (enabled: boolean) => set({ isResponseNeededEnabled: enabled }),
  setAgentSalesStageEnabled: (enabled: boolean) => set({ agentSalesStageEnabled: enabled }),
  setAgentSpeechToneEnabled: (enabled: boolean) => set({ agentSpeechToneEnabled: enabled }),
  setAgentSummarizerEnabled: (enabled: boolean) => set({ agentSummarizerEnabled: enabled }),

  // UI state management actions
  setSavingCredentials: (saving: boolean) => set({ savingCredentials: saving }),
  setCredentialsError: (error: string | null) => set({ credentialsError: error }),
  setCredentialsSuccess: (success: string | null) => set({ credentialsSuccess: success }),

  // Reset functions
  resetCredentialsState: () => set({
    savingCredentials: false,
    credentialsError: null,
    credentialsSuccess: null,
  }),

  resetProfile: () => set({
    profile: null,
    profileLoading: false,
    profileError: null,
    totalCalls: 0,
    lastActivity: null,
    voicenterLogin: '',
    voicecenterPassword: '',
    defaultDeviceIdSalesman: '',
    defaultDeviceIdClient: '',
    transcriptionVisible: false,
    isResponseNeededEnabled: false,
    agentSalesStageEnabled: false,
    agentSpeechToneEnabled: false,
    agentSummarizerEnabled: false,
    savingCredentials: false,
    credentialsError: null,
    credentialsSuccess: null,
    termsAcceptedAt: null,
  }),

  acceptTerms: async () => {
    const { data: userData, error } = await supabase.auth.getUser();
    if (error || !userData?.user?.id) {
      throw new Error(error?.message || 'No authenticated user');
    }
    const profileService = new ProfileService();
    const acceptedAt = await profileService.updateTermsAcceptedAt(userData.user.id);
    set({ termsAcceptedAt: acceptedAt });
  },
}), {
  name: 'profile-settings-store',
  partialize: (state) => ({
    // Persist certain optimistic UI states or preferences that shouldn't flicker on reload
    transcriptionVisible: state.transcriptionVisible,
    micMinDbFS: state.micMinDbFS,
    callProvider: state.callProvider,
    language: state.language,
    defaultDeviceIdSalesman: state.defaultDeviceIdSalesman,
    defaultDeviceIdClient: state.defaultDeviceIdClient,
  } as unknown as ProfileSettingsStore),
}));
